"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { ListFilter, SlidersHorizontal, X } from "lucide-react";
import {
  CAR_FILTERS,
  BRANDS,
  modelsForBrand,
  CAR_COLOR_OPTIONS,
  ENGINE_VOLUME_OPTIONS,
  BODY_TYPE_OPTIONS,
  carYearOptions,
} from "@/lib/constants";
import { useCatalogFilterNavigate, useDeferredCatalogParam } from "@/lib/hooks/use-catalog-filter-navigate";
import { cn } from "@/lib/utils";
import Button from "@/components/ui/Button";

function countActiveCatalogFilters(params: URLSearchParams): number {
  let n = 0;
  params.forEach((v) => {
    if (v !== "") n += 1;
  });
  return n;
}

export default function CarFilters({ 
  basePath = "/cars",
  defaultBrand,
  defaultModel,
}: { 
  basePath?: string;
  defaultBrand?: string;
  defaultModel?: string;
}) {
  const t = useTranslations("CarFilters");
  const tCat = useTranslations("Catalog");
  const { setParam, clearAll, searchParams } = useCatalogFilterNavigate(basePath, defaultBrand);
  const [sheetOpen, setSheetOpen] = useState(false);

  const brand = searchParams.get("brand") ?? defaultBrand ?? "";
  const modelList = brand ? modelsForBrand(brand) : [];
  const curModel = searchParams.get("model") ?? defaultModel ?? "";
  const modelOpts = curModel && !modelList.includes(curModel) ? [...modelList, curModel] : modelList;
  const yearOpts = carYearOptions();

  const { local: minPriceLocal, onChange: onMinPriceChange } = useDeferredCatalogParam(
    "min_price",
    setParam,
    searchParams,
  );
  const { local: maxPriceLocal, onChange: onMaxPriceChange } = useDeferredCatalogParam(
    "max_price",
    setParam,
    searchParams,
  );

  const activeCount = useMemo(() => countActiveCatalogFilters(searchParams), [searchParams]);

  useEffect(() => {
    if (!sheetOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [sheetOpen]);

  useEffect(() => {
    if (!sheetOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSheetOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [sheetOpen]);

  function fuelBtnLabel(f: (typeof CAR_FILTERS.fuel_type)[number]) {
    if (f === "petrol") return t("fuelPetrol");
    if (f === "diesel") return t("fuelDiesel");
    if (f === "electric") return t("fuelElectric");
    return t("fuelHybrid");
  }

  const filterFields = (
    <>
      <section className="space-y-3 border-t border-gray-100 pt-4 first:border-t-0 first:pt-0 dark:border-gray-800">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{t("sectionBrandModel")}</h4>
          <Link 
            href="/cars/brands"
            className="text-xs font-medium text-[#E63946] hover:underline dark:text-[#ff6b7a]"
          >
            Все марки
          </Link>
        </div>
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">{t("brand")}</label>
          <select
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#E63946] focus:ring-2 focus:ring-[#E63946]/15 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
            value={searchParams.get("brand") ?? ""}
            onChange={(e) => setParam("brand", e.target.value)}
          >
            <option value="">{t("allBrands")}</option>
            {BRANDS.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">{t("model")}</label>
          <select
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#E63946] focus:ring-2 focus:ring-[#E63946]/15 disabled:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:disabled:bg-gray-800"
            value={searchParams.get("model") ?? ""}
            onChange={(e) => setParam("model", e.target.value)}
            disabled={!brand}
          >
            <option value="">{brand ? t("allModels") : t("pickBrandFirst")}</option>
            {modelOpts.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>
      </section>

      <section className="space-y-3 border-t border-gray-100 pt-4 dark:border-gray-800">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{t("sectionTripZone")}</h4>
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">{t("tripZone")}</label>
          <select
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#E63946] focus:ring-2 focus:ring-[#E63946]/15 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
            value={searchParams.get("trip_zone") ?? ""}
            onChange={(e) => setParam("trip_zone", e.target.value)}
          >
            <option value="">{t("tripZoneAny")}</option>
            <option value="within">{t("tripZoneWithin")}</option>
            <option value="outside">{t("tripZoneOutside")}</option>
          </select>
        </div>
      </section>

      <section className="space-y-3 border-t border-gray-100 pt-4 dark:border-gray-800">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{t("sectionYearColor")}</h4>
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">{t("year")}</label>
          <select
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#E63946] focus:ring-2 focus:ring-[#E63946]/15 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
            value={searchParams.get("year") ?? ""}
            onChange={(e) => setParam("year", e.target.value)}
          >
            <option value="">{t("any")}</option>
            {yearOpts.map((y) => (
              <option key={y} value={String(y)}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">{t("color")}</label>
          <select
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#E63946] focus:ring-2 focus:ring-[#E63946]/15 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
            value={searchParams.get("color") ?? ""}
            onChange={(e) => setParam("color", e.target.value)}
          >
            <option value="">{t("any")}</option>
            {CAR_COLOR_OPTIONS.map((c) => (
              <option key={c.value} value={c.value}>
                {tCat(`color.${c.value}`)}
              </option>
            ))}
          </select>
        </div>
      </section>

      <section className="space-y-3 border-t border-gray-100 pt-4 dark:border-gray-800">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{t("sectionSpecs")}</h4>
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">{t("engine")}</label>
          <select
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#E63946] focus:ring-2 focus:ring-[#E63946]/15 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
            value={searchParams.get("engine_volume") ?? ""}
            onChange={(e) => setParam("engine_volume", e.target.value)}
          >
            <option value="">{t("any")}</option>
            {ENGINE_VOLUME_OPTIONS.map((v) => (
              <option key={v} value={v}>
                {v === "electric" ? t("engineElectric") : t("engineLiters", { vol: v })}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">{t("body")}</label>
          <select
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#E63946] focus:ring-2 focus:ring-[#E63946]/15 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
            value={searchParams.get("body_type") ?? ""}
            onChange={(e) => setParam("body_type", e.target.value)}
          >
            <option value="">{t("any")}</option>
            {BODY_TYPE_OPTIONS.map((bt) => (
              <option key={bt.value} value={bt.value}>
                {tCat(`body.${bt.value}`)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">{t("transmission")}</label>
          <div className="flex gap-2">
            {CAR_FILTERS.transmission.map((trans) => (
              <button
                key={trans}
                type="button"
                onClick={() =>
                  setParam("transmission", searchParams.get("transmission") === trans ? "" : trans)
                }
                className={cn(
                  "flex-1 py-2 text-xs font-medium rounded-lg border transition-colors",
                  searchParams.get("transmission") === trans
                    ? "border-[#E63946] bg-[#E63946]/10 text-[#E63946]"
                    : "border-gray-200 text-gray-600 hover:border-gray-300 active:scale-[0.98] dark:border-gray-700 dark:text-gray-400 dark:hover:border-gray-600",
                )}
              >
                {trans === "manual" ? t("transManual") : t("transAutomatic")}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">{t("fuel")}</label>
          <div className="grid grid-cols-2 gap-2">
            {CAR_FILTERS.fuel_type.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setParam("fuel_type", searchParams.get("fuel_type") === f ? "" : f)}
                className={cn(
                  "py-2 text-xs font-medium rounded-lg border transition-colors",
                  searchParams.get("fuel_type") === f
                    ? "border-[#E63946] bg-[#E63946]/10 text-[#E63946]"
                    : "border-gray-200 text-gray-600 hover:border-gray-300 active:scale-[0.98] dark:border-gray-700 dark:text-gray-400 dark:hover:border-gray-600",
                )}
              >
                {fuelBtnLabel(f)}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="space-y-3 border-t border-gray-100 pt-4 dark:border-gray-800">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{t("sectionPrice")}</h4>
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">{t("pricePerDay")}</label>
          <div className="flex gap-2">
            <input
              type="number"
              inputMode="numeric"
              min={0}
              placeholder={t("from")}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#E63946] focus:ring-2 focus:ring-[#E63946]/15 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
              value={minPriceLocal}
              onChange={(e) => onMinPriceChange(e.target.value)}
            />
            <input
              type="number"
              inputMode="numeric"
              min={0}
              placeholder={t("to")}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#E63946] focus:ring-2 focus:ring-[#E63946]/15 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
              value={maxPriceLocal}
              onChange={(e) => onMaxPriceChange(e.target.value)}
            />
          </div>
        </div>
      </section>
    </>
  );

  return (
    <>
      <aside className="hidden lg:block w-full lg:w-64 shrink-0">
        <div className="lg:sticky lg:top-24">
          <div className="space-y-6 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900/60">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">{t("title")}</h3>
              <button type="button" onClick={clearAll} className="text-xs text-[#E63946] hover:underline shrink-0">
                {t("reset")}
              </button>
            </div>
            {filterFields}
          </div>
        </div>
      </aside>

      <div className="lg:hidden w-full shrink-0">
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          className={cn(
            "flex w-full items-center justify-between gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3.5 dark:border-gray-700 dark:bg-gray-900/80",
            "text-left shadow-sm transition-transform active:scale-[0.99] dark:text-gray-100",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E63946] focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-950",
          )}
          aria-expanded={sheetOpen}
          aria-haspopup="dialog"
        >
          <span className="flex items-center gap-3 min-w-0">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E63946]/10 text-[#E63946] shrink-0">
              <SlidersHorizontal size={20} strokeWidth={2} aria-hidden />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">{t("openFilters")}</span>
              <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                {activeCount > 0 ? t("activeFiltersCount", { count: activeCount }) : t("mobileFiltersHint")}
              </span>
            </span>
          </span>
          {activeCount > 0 ? (
            <span className="flex h-7 min-w-[1.75rem] items-center justify-center rounded-full bg-[#E63946] px-2 text-xs font-bold text-white shrink-0">
              {activeCount}
            </span>
          ) : (
            <ListFilter size={18} className="shrink-0 text-gray-400 dark:text-gray-500" aria-hidden />
          )}
        </button>
      </div>

      {sheetOpen ? (
        <div
          className="lg:hidden fixed inset-0 z-[60] flex flex-col justify-end"
          role="dialog"
          aria-modal="true"
          aria-labelledby="catalog-filters-sheet-title"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/45 backdrop-blur-[1px]"
            aria-label={t("sheetCloseAria")}
            onClick={() => setSheetOpen(false)}
          />
          <div
            className="relative flex max-h-[min(92dvh,920px)] flex-col rounded-t-[1.25rem] bg-white shadow-[0_-12px_40px_rgba(0,0,0,0.12)] animate-[catalog-sheet-in_0.28s_cubic-bezier(0.32,0.72,0,1)_forwards] dark:bg-gray-900 dark:shadow-[0_-12px_40px_rgba(0,0,0,0.45)]"
          >
            <div className="flex shrink-0 justify-center pb-1 pt-2" aria-hidden>
              <div className="h-1 w-10 rounded-full bg-gray-200 dark:bg-gray-600" />
            </div>
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-100 px-4 pb-3 dark:border-gray-800">
              <div className="min-w-0">
                <h2 id="catalog-filters-sheet-title" className="truncate text-base font-bold text-gray-900 dark:text-gray-100">
                  {t("sheetTitle")}
                </h2>
                {activeCount > 0 ? (
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{t("activeFiltersCount", { count: activeCount })}</p>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  onClick={clearAll}
                  className="px-2 py-1 text-xs font-medium text-[#E63946] hover:underline"
                >
                  {t("reset")}
                </button>
                <button
                  type="button"
                  onClick={() => setSheetOpen(false)}
                  className="flex h-10 w-10 items-center justify-center rounded-xl text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                  aria-label={t("sheetCloseAria")}
                >
                  <X size={22} />
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 space-y-0 overflow-y-auto overscroll-contain px-4 pb-2 pt-2">
              <div className="space-y-6 pb-2">{filterFields}</div>
            </div>

            <div className="shrink-0 border-t border-gray-100 bg-white px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] dark:border-gray-800 dark:bg-gray-900">
              <Button type="button" size="lg" className="w-full gap-2" onClick={() => setSheetOpen(false)}>
                {t("sheetDone")}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
